// src/pages/LoginPage.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../context/AuthContext";

// ✅ Use your centralized API client + auth APIs
import { loginUser, getCurrentUser, getContextOptions } from "../api/authAPI";

const SESSION_END_TOAST_KEY = "sessionEndedMessage";

const getPreferredAccountingClientId = (options, tenantType, role) => {
  const safeOptions = Array.isArray(options) ? options : [];

  // billing_ff is locked to their single assigned client — just take the first returned option
  // (context/options already filters to only their assigned client).
  if (role === 'billing_ff') {
    return safeOptions[0]?._id || null;
  }

  const billingClient = safeOptions.find((option) => option?.clientCode === "CL001");

  if (tenantType === "billing" && billingClient?._id) {
    return billingClient._id;
  }

  const preferredClient =
    safeOptions.find(
      (option) => option?.clientType === "billing" && option?.clientCode === "CL001"
    );

  return preferredClient?._id || safeOptions[0]?._id || null;
};

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [sessionNotice, setSessionNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingRedirectToken, setPendingRedirectToken] = useState(null);

  const navigate = useNavigate();
  const { login, setAccessToken, isAuthenticated, getAccessToken } = useAuth();

  const pageName = import.meta.env.VITE_LANDING_PAGE_NAME || "Venta Hub Login";

  // -----------------------------
  // Helpers
  // -----------------------------
  const redirectBasedOnRole = (token) => {
    try {
      const decoded = jwtDecode(token);
      const tenantType =
        decoded?.tenantType ||
        decoded?.tenant_Type ||
        localStorage.getItem("tenantType");

      switch (decoded.role) {
        case "superadmin":
          navigate("/superadmin-dashboard");
          break;
        case "cashier":
          navigate("/sales-order");
          break;
        case "billing_ff":
          navigate("/billing/ff-billing");
          break;
        default:
          navigate(tenantType === "accounting_operations" ? "/ao" : "/client-dashboard");
      }
    } catch (err) {
      console.error("Error decoding token:", err);
      handleLogout();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("tenantId");
    localStorage.removeItem("storeId");
    localStorage.removeItem("accountingClientId");
    localStorage.removeItem("tenantType");
    localStorage.removeItem("contextKind");
    localStorage.removeItem("rememberMe");
    localStorage.removeItem("rememberedUsername");
  };

  const togglePasswordVisibility = () => setShowPassword((s) => !s);

  const handleRememberMeChange = (e) => {
    const isChecked = e.target.checked;
    setRememberMe(isChecked);

    if (!isChecked) {
      localStorage.removeItem("rememberedUsername");
      localStorage.removeItem("rememberMe");
    } else {
      localStorage.setItem("rememberMe", "true");
      if (username.trim()) localStorage.setItem("rememberedUsername", username.trim());
    }
  };

  // -----------------------------
  // On mount
  // -----------------------------
  useEffect(() => {
    const endedMessage = sessionStorage.getItem(SESSION_END_TOAST_KEY);
    if (endedMessage) {
      setSessionNotice(endedMessage);
      sessionStorage.removeItem(SESSION_END_TOAST_KEY);
    }

    // restore remember-me username
    const savedUsername = localStorage.getItem("rememberedUsername");
    const savedRememberMe = localStorage.getItem("rememberMe") === "true";
    if (savedUsername && savedRememberMe) {
      setUsername(savedUsername);
      setRememberMe(true);
    }

    // already logged in?
    if (isAuthenticated && getAccessToken()) {
      redirectBasedOnRole(getAccessToken());
    }

    // Prevent back navigation after logout (keep your behavior)
    window.history.pushState(null, "", window.location.href);
    window.onpopstate = () => window.history.pushState(null, "", window.location.href);

    return () => {
      window.onpopstate = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep remembered username fresh
  useEffect(() => {
    if (rememberMe && username.trim()) {
      localStorage.setItem("rememberedUsername", username.trim());
    }
  }, [username, rememberMe]);

  // Navigate only after AuthContext confirms authenticated state.
  useEffect(() => {
    if (!pendingRedirectToken || !isAuthenticated) return;
    redirectBasedOnRole(pendingRedirectToken);
    setPendingRedirectToken(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRedirectToken, isAuthenticated]);

  // -----------------------------
  // Submit
  // -----------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // 1) Login (stores token in localStorage via authAPI)
      const loginRes = await loginUser({ username: username.trim(), password }, setAccessToken);
      const token = loginRes?.accessToken || loginRes?.token;

      if (!token) throw new Error("No token received from server");

      // 2) Bootstrap app session with /me
      const me = await getCurrentUser();

      const tenantId = me?.tenant?._id || null;
      const tenantType = me?.tenant?.tenantType || null;
      if (tenantId) localStorage.setItem("tenantId", tenantId);
      if (tenantType) localStorage.setItem("tenantType", tenantType);

      // Clear context first (prevents store leakage into accounting)
      localStorage.removeItem("contextKind");
      localStorage.removeItem("storeId");
      localStorage.removeItem("accountingClientId");

      // 3) Load context options and set a default context
      if (me?.contextKind) {
        localStorage.setItem("contextKind", me.contextKind);
        const ctx = await getContextOptions(); // { kind, options }

        if (ctx?.kind === "store") {
          const storeId = ctx?.options?.[0]?._id || null;
          if (storeId) localStorage.setItem("storeId", storeId);
        } else if (ctx?.kind === "accounting_client") {
          const loginRole = me?.user?.role || loginRes?.role || null;
          const accountingClientId = getPreferredAccountingClientId(ctx?.options, tenantType, loginRole);
          if (accountingClientId) localStorage.setItem("accountingClientId", accountingClientId);
        }
      }

      // 4) Update your AuthContext
      // Pass full user payload to AuthContext to avoid partial state during first render.
      const userId = me?.user?._id || loginRes?._id || loginRes?.id || null;
      const authUserData = me?.user || loginRes?.user || {
        _id: userId,
        id: userId,
        role: loginRes?.role,
        username: loginRes?.username,
      };
      const storeId = localStorage.getItem("storeId") || null;

      login(token, authUserData, tenantId, storeId, loginRes?.refreshToken);

      // 5) Remember me settings
      if (rememberMe) {
        localStorage.setItem("rememberedUsername", username.trim());
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("rememberedUsername");
        localStorage.removeItem("rememberMe");
      }

      // 6) Redirect only after auth state propagation completes.
      setPendingRedirectToken(token);
    } catch (err) {
      console.error("Login error:", err);

      // Try to extract backend error message
      const msg =
        err?.response?.data?.message ||
        (typeof err === "string" ? err : null) ||
        err?.message ||
        "Invalid credentials. Please try again.";

      setError(msg);
      handleLogout(true);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // UI
  // -----------------------------
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded shadow">
        <h2 className="text-2xl font-bold text-center">{pageName}</h2>

        {sessionNotice && (
          <div className="p-3 text-sm text-teal-800 bg-teal-50 border border-teal-200 rounded-md">
            {sessionNotice}
          </div>
        )}

        {error && (
          <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-teal-300"
              required
              disabled={loading}
              placeholder="Enter your username"
            />
          </div>

          <div className="relative">
            <label className="block text-xs font-medium text-gray-700">Password</label>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-teal-300 pr-10"
              required
              disabled={loading}
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute inset-y-0 right-0 flex items-center pr-3 pt-4 text-gray-600 hover:text-gray-800"
              disabled={loading}
              aria-label="Toggle password visibility"
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={handleRememberMeChange}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                disabled={loading}
              />
              <span className="text-sm text-gray-700">Remember me</span>
            </label>

            <button
              type="button"
              className="text-sm text-teal-600 hover:text-teal-800 focus:outline-none"
              disabled={loading}
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            className={`w-full px-4 py-2 font-bold text-white transition-all duration-300 rounded flex items-center justify-center ${
              loading
                ? "bg-gray-500 cursor-not-allowed"
                : "bg-teal-500 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50"
            }`}
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <span className="pr-2">Logging in</span>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              "Login"
            )}
          </button>

          <p className="text-xs text-gray-500 text-center">
            By signing in, you agree to the{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="text-teal-600 hover:text-teal-800 underline"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="/privacy"
              target="_blank"
              rel="noreferrer"
              className="text-teal-600 hover:text-teal-800 underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
